import * as Location from 'expo-location';
import { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Linking, Platform } from 'react-native';

import { Logger } from '@/utils/logging';

type LocationState = {
  fetching: boolean;
  status: Location.PermissionStatus;
  canAskAgain: boolean;
  location: Location.LocationObject | null;
  error: string | null;
};

type LocationAction =
  | {
      type: 'request';
      payload?: never;
    }
  | {
      type: 'denied';
      payload: { error: string; canAskAgain: boolean };
    }
  | {
      type: 'granted';
      payload?: never;
    }
  | {
      type: 'location';
      payload: { location: Location.LocationObject };
    };

function locationReducer(state: LocationState, action: LocationAction) {
  switch (action.type) {
    case 'denied':
      return {
        ...state,
        fetching: false,
        location: null,
        status: Location.PermissionStatus.DENIED,
      };
    case 'granted':
      return {
        ...state,
        fetching: false,
        status: Location.PermissionStatus.GRANTED,
      };
    case 'location':
      return {
        ...state,
        location: action.payload.location,
      };
    case 'request':
      return {
        ...state,
        fetching: true,
      };
    default:
      return state;
  }
}

function showAlert(errorMessage: string, openSettingsText: string) {
  if (Platform.OS === 'web') {
    window.alert(errorMessage);
  } else {
    Alert.alert(errorMessage, errorMessage, [
      {
        text: openSettingsText,
        onPress: Linking.openSettings,
      },
    ]);
  }
}

export function useUserLocation(options: {
  requestPermissionOnMount?: boolean;
}) {
  const { t } = useTranslation('common', {
    keyPrefix: 'shared.locationPermission',
  });
  const [locationState, dispatch] = useReducer(locationReducer, {
    error: null,
    location: null,
    fetching: false,
    canAskAgain: true,
    status: Location.PermissionStatus.UNDETERMINED,
  });

  const getCurrentLocation = useCallback(async () => {
    const location = await Location.getCurrentPositionAsync();
    dispatch({ type: 'location', payload: { location } });

    return location;
  }, []);

  /**
   * Resolves whether location was granted and never rejects: a denial is an
   * expected outcome, and callers fire this from press handlers.
   */
  const requestLocationPermission = useCallback(
    async (getLocationAfterPermissionGranted: boolean = false) => {
      dispatch({ type: 'request' });

      let response: Location.LocationPermissionResponse | null = null;
      try {
        response = await Location.requestForegroundPermissionsAsync();
      } catch (error) {
        // Browsers without the Permissions API (old Safari, some in-app
        // webviews) throw here, so web warns; on native a throw is a bug.
        const log = Platform.OS === 'web' ? Logger.warn : Logger.error;
        log('Location permission request failed', { error });
      }

      if (!response?.granted) {
        const canAskAgain = response?.canAskAgain ?? false;
        const errorMessage = t('denied');
        const shouldShowSettingsAlert = Platform.select({
          web: locationState.status === Location.PermissionStatus.DENIED,
          default: !canAskAgain,
        });

        if (shouldShowSettingsAlert) {
          showAlert(errorMessage, t('openSettings'));
        }

        dispatch({
          type: 'denied',
          payload: { canAskAgain, error: errorMessage },
        });

        return false;
      }

      dispatch({ type: 'granted' });
      if (getLocationAfterPermissionGranted) {
        await getCurrentLocation();
      }
      return true;
    },
    [getCurrentLocation, locationState.status, t],
  );

  useEffect(() => {
    if (options.requestPermissionOnMount) {
      void requestLocationPermission();
    }

    Location.getForegroundPermissionsAsync()
      .then(({ canAskAgain, granted }) => {
        dispatch(
          granted
            ? {
                type: 'granted',
              }
            : {
                type: 'denied',
                payload: { canAskAgain, error: '' },
              },
        );
      })
      .catch((error: unknown) => {
        // Unsupported in some webviews; the status stays undetermined.
        const log = Platform.OS === 'web' ? Logger.warn : Logger.error;
        log('Location permission check failed', { error });
      });
  }, [options.requestPermissionOnMount, requestLocationPermission]);

  return {
    getCurrentLocation,
    requestLocationPermission,
    status: locationState.status,
    canAskAgain: locationState.canAskAgain,
    location: locationState.location,
    error: locationState.error,
    fetching: locationState.fetching,
  };
}
